import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { RequestsService } from '../../../requests/services/requests.service';
import { SubstituteService } from '../../../shared/services/substitute.service';
import { UserService } from '../../../shared/services/user.service';
import { UtilsService } from '../../../shared/services/utils.service';
import { AddSubstituteData } from './add-substitute.types';

@Component({
  selector: 'app-add-substitute',
  templateUrl: './add-substitute.component.html',
  styleUrls: ['./add-substitute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddSubstituteComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private loadingSubject = new BehaviorSubject(false);
  private errorSubject = new BehaviorSubject(null);

  form = this.fb.group({
    is_permanent: this.fb.control(false, []),
    startDate: this.fb.control(null, [Validators.required]),
    endDate: this.fb.control(null, [
      (ac: AbstractControl) => {
        if (!ac.parent?.get('is_permanent')?.value) {
          return Validators.required(ac);
        }

        return null;
      }
    ]),
    substituteUser: this.fb.control([], [Validators.required]),
    user: this.fb.control(null, [Validators.required])
  });

  loading$ = this.loadingSubject.asObservable();
  error$ = this.errorSubject.asObservable().pipe(distinctUntilChanged());
  canEditUser = Promise.resolve(this.data.scope === 'all');

  constructor(
    private fb: FormBuilder,
    private requestService: RequestsService,
    private dialogRef: MatDialogRef<AddSubstituteComponent>,
    private substituteService: SubstituteService,
    private userService: UserService,
    private utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data: AddSubstituteData
  ) {}

  ngOnInit(): void {
    const user = this.form.get('user');
    user.setValue(this.userService.getUserDetails());
    user.disable();

    this.canEditUser.then(result => {
      if (result) {
        this.form.get('user').enable();
      }
    });

    this.form
      .get('is_permanent')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(isPermanent => {
        if (isPermanent) {
          this.form.get('endDate').reset();
          this.form.get('endDate').disable();
        } else {
          this.form.get('endDate').enable();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onSubmit(): void {
    this.loadingSubject.next(true);
    combineLatest([
      ...this.form.get('substituteUser').value.map(substituteUser =>
        this.substituteService.add$(this.data.scope, this.form.get('user').value?.userName, substituteUser, {
          endDate: this.form.get('endDate').value,
          startDate: this.form.get('startDate').value
        }).pipe(catchError(e => of(e)))
      )
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (responses) => {
          // we should close the form in case of one successful response
          const hasSuccessResponse = responses.some(r => !(r instanceof HttpErrorResponse));
          this.loadingSubject.next(false);
          if(hasSuccessResponse) {
            this.dialogRef.close(true);
          }
        },
        error => {
          this.loadingSubject.next(false);
          this.errorSubject.next(error.error.message);
        }
      );
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
